import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '../services/api';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Passordet må være minst 8 tegn langt');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passordene matcher ikke');
      return;
    }

    if (!token) {
      setError('Ugyldig tilbakestillingslenke');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError('Tilbakestillingslenken er ugyldig eller utløpt');
      } else {
        setError('En feil oppstod. Vennligst prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center bg-gradient-to-br from-[#667eea] to-[#764ba2]">
        <div className="mx-auto w-full max-w-sm px-4">
          <Card className="rounded-xl p-8 shadow-2xl">
            <Alert variant="destructive">
              <AlertDescription>
                Ugyldig tilbakestillingslenke. Vennligst be om en ny lenke.
              </AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/forgot-password')} className="mt-6 w-full">
              Be om ny lenke
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center bg-gradient-to-br from-[#667eea] to-[#764ba2]">
      <div className="mx-auto w-full max-w-sm px-4">
        <Card className="rounded-xl p-8 shadow-2xl">
          {success ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-4 h-20 w-20 text-status-confirmed" />
              <h1 className="mb-2 text-2xl font-bold text-foreground">Passordet er tilbakestilt!</h1>
              <p className="mb-6 text-sm text-muted-foreground">
                Du blir omdirigert til innloggingssiden...
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Gå til innlogging
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="mb-2 text-2xl font-bold text-foreground">Opprett nytt passord</h1>
                <p className="text-sm text-muted-foreground">Skriv inn ditt nye passord nedenfor.</p>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password-input">Nytt passord</Label>
                  <div className="relative">
                    <Input
                      id="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Skjul passord' : 'Vis passord'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Minimum 8 tegn</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password-input">Bekreft passord</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirmPassword ? 'Skjul passord' : 'Vis passord'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" size="lg" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Tilbakestill passord'}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
